import { memo, useEffect } from 'react';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground, usePageDispatch } from '../../../context/Page/page';
import { CTAButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { useAppointmentState } from '../../../context/Appointment/appointment';
import { useDisplayLocalizedVehicle } from '../../../helpers/displayVehicle';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { useRestartOnBackAfterSignature } from '../../../helpers/Hooks';
import { Types } from '../../../context/Page/page.types';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { TowInCompleteProps } from './TowInComplete.types';
import { GoMotoComponent } from '../../../API/GoMoto.types';

/**
 * Displays information about the tow-in appointment and completes the appointment
 * when the user proceeds from this screen.
 *
 * @param onComplete - {@link TowInCompleteProps.onComplete onComplete} description.
 */
export const TowInComplete: GoMotoComponent<TowInCompleteProps> = memo(({
    onComplete,
}) => {
    useRestartOnBackAfterSignature();
    const hideKioskBackground = useHideKioskBackground();
    const Localization = useLocalization();
    const { kioskType } = useKioskState();
    const pageDispatch = usePageDispatch();
    const appointment = useAppointmentState();
    const isKiosk = isElectron();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();

    useEffect(() => {
        if (kioskType !== 'outdoor') {
            pageDispatch({
                type: Types.HideIdleTimeoutDialog,
                payload: true,
            });

            return () => {
                pageDispatch({
                    type: Types.HideIdleTimeoutDialog,
                    payload: false,
                });
            };
        }

        return;
    }, [pageDispatch, kioskType]);

    useEffect(() => {
        hideKioskBackground();
    }, [hideKioskBackground]);

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                            <GMSizedIcon
                                iconType={'Complete'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.CompleteCheckmarkIcon}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.AllDone}
                    secondaryHeading={isKiosk ? (
                        <>
                            <div>
                                {Localization.YourDropOffIsComplete}
                            </div>
                            <div>
                                {Localization.formatString(Localization.YourDropOffIsCompleteMessage, <b>{displayLocalizedVehicle(appointment, true)}</b>)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                {Localization.YourDropOffIsComplete}
                                {' '}
                                {Localization.formatString(Localization.YourDropOffIsCompleteMessage, <b>{displayLocalizedVehicle(appointment, true)}</b>)}
                            </div>
                        </>
                    )}
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            styleType={'primary'}
                            onClick={onComplete}
                            title={Localization.ClickToConfirmDropOffComplete}
                            label={Localization.Finish}
                            kioskOptions={{
                                inlineOptions: {
                                    sizedIcon: (
                                        <GMSizedIcon
                                            iconSize={'custom'}
                                            iconType={'Complete'}
                                            ariaLabel={Localization.CompleteCheckmarkIcon}
                                        />
                                    ),
                                },
                            }}
                            id={Localization.ClickToConfirmDropOffComplete}
                            ariaLabel={Localization.ClickToConfirmDropOffComplete}
                        />,
                    ]}
                />
            }
        >
            <></>
        </ScrollableBody>
    );
});
